/*
 * Layout shell — mirrors Dashboard.html .app > (.ds-sidebar + .ds-topbar + .ds-content)
 * from claude_design_output_v2/sensor/project/Dashboard.html lines 72-289.
 *
 * Scope of this PR (web/p1-design-tokens-and-shell):
 * - Sidebar: logo, nav, settings entry, trial mini-widget, logout.
 * - Topbar: breadcrumb (live-derived from current route), search (visual only),
 *   "Open bot" link, notifications icon (no popover yet), avatar.
 *
 * Out of scope (deferred to follow-up PRs):
 * - Org switcher popover (#org-trigger / #org-pop)
 * - Notifications popover (#notif-pop)
 * - Sticky trial-banner under topbar (.ds-trialbar)
 * - RU/EN language switcher behavior
 * - Calibration overlay
 */
import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'

const NAV_WORKPLACE = [
  {
    to: '/dashboard',
    label: 'Сегодня',
    count: undefined as number | undefined,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    ),
  },
  {
    to: '/projects',
    label: 'Проекты',
    count: undefined,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      </svg>
    ),
  },
  {
    to: '/leads',
    label: 'Лиды',
    count: undefined,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M4 6h16M4 12h16M4 18h10" />
      </svg>
    ),
  },
  {
    to: '/sources',
    label: 'Источники',
    count: undefined,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
      </svg>
    ),
  },
  {
    to: '/audience',
    label: 'Аудитория',
    count: undefined,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M16 11a4 4 0 1 0-8 0" />
        <path d="M4 21a8 8 0 0 1 16 0" />
        <path d="M18 7.5a2.5 2.5 0 0 1 0 5" />
        <path d="M6 7.5a2.5 2.5 0 0 0 0 5" />
      </svg>
    ),
  },
  {
    to: '/chat',
    label: 'Чат по источникам',
    count: undefined,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8A8.5 8.5 0 1 1 21 11.5z" />
      </svg>
    ),
  },
] as const

const ROUTE_LABEL: Record<string, string> = {
  '/dashboard': 'Сегодня',
  '/projects': 'Проекты',
  '/leads': 'Лиды',
  '/sources': 'Источники',
  '/audience': 'Аудитория',
  '/admin/senders': 'Senders (админ)',
  '/chat': 'Чат по источникам',
  '/settings': 'Настройки',
}

const SIDEBAR_WIDTH_KEY = 'sensor.shell.sidebarWidth'
const SIDEBAR_WIDTH_MIN = 220
const SIDEBAR_WIDTH_MAX = 360
const SIDEBAR_WIDTH_DEFAULT = 248

function deriveCrumb(pathname: string): string {
  // Exact-match first; if nested route, take the first segment.
  if (ROUTE_LABEL[pathname]) return ROUTE_LABEL[pathname]
  const first = '/' + pathname.split('/').filter(Boolean)[0]
  return ROUTE_LABEL[first] ?? 'Дашборд'
}

export function Layout() {
  const { user, logout } = useAuth()
  const { lang, setLang } = useI18n()
  const location = useLocation()
  const crumb = deriveCrumb(location.pathname)
  const userIdLabel = user ? `ID ${user.telegram_user_id}` : ''
  const actorLabel = user
    ? user.telegram_display_name?.trim() ||
      (user.telegram_username ? `@${user.telegram_username.replace(/^@+/, '')}` : '') ||
      userIdLabel
    : ''

  // Resolve the real organization name: prefer the one matching the active org,
  // fall back to the first membership (covers stale sessions bound to org 0).
  const activeOrg =
    user?.organizations?.find((o) => o.id === user.active_organization_id) ??
    user?.organizations?.[0]
  const savedOrgLabel = activeOrg?.name?.trim() ?? ''
  const orgLabel = !savedOrgLabel || savedOrgLabel.startsWith('@')
    ? actorLabel || savedOrgLabel || 'Организация'
    : savedOrgLabel
  const orgBadgeSource = orgLabel.trim().replace(/^@+/, '').trim()
  const orgBadge = orgBadgeSource.charAt(0).toUpperCase() || 'O'

  // Avatar dropdown menu (top-right).
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!menuOpen) return
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [menuOpen])

  // Org switcher popover (sidebar).
  const [orgMenuOpen, setOrgMenuOpen] = useState(false)
  const orgMenuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!orgMenuOpen) return
    function onDocClick(e: MouseEvent) {
      if (orgMenuRef.current && !orgMenuRef.current.contains(e.target as Node)) {
        setOrgMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [orgMenuOpen])

  const orgs = user?.organizations ?? []
  const appRef = useRef<HTMLDivElement>(null)
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window === 'undefined') return SIDEBAR_WIDTH_DEFAULT
    const stored = Number(window.localStorage.getItem(SIDEBAR_WIDTH_KEY))
    return Number.isFinite(stored) && stored >= SIDEBAR_WIDTH_MIN && stored <= SIDEBAR_WIDTH_MAX
      ? Math.round(stored)
      : SIDEBAR_WIDTH_DEFAULT
  })
  const [isSidebarDragging, setIsSidebarDragging] = useState(false)

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth))
  }, [sidebarWidth])

  function startSidebarResize(e: ReactPointerEvent<HTMLDivElement>) {
    const rect = appRef.current?.getBoundingClientRect()
    if (!rect) return
    e.preventDefault()
    setIsSidebarDragging(true)

    const onMove = (event: PointerEvent) => {
      const next = Math.max(
        SIDEBAR_WIDTH_MIN,
        Math.min(SIDEBAR_WIDTH_MAX, Math.round(event.clientX - rect.left)),
      )
      setSidebarWidth(next)
    }
    const onUp = () => {
      setIsSidebarDragging(false)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  return (
    <div
      ref={appRef}
      className="app"
      style={{ ['--sidebar-w' as string]: `${sidebarWidth}px` }}
    >
      {/* ============================================================
          Sidebar
          ============================================================ */}
      <aside className="ds-sidebar" aria-label="Основная навигация">
        <div
          className={`ds-sidebar-resizer${isSidebarDragging ? ' is-dragging' : ''}`}
          onPointerDown={startSidebarResize}
          role="separator"
          aria-orientation="vertical"
          aria-valuemin={SIDEBAR_WIDTH_MIN}
          aria-valuemax={SIDEBAR_WIDTH_MAX}
          aria-valuenow={sidebarWidth}
          aria-label="Изменить ширину меню"
          title="Потяните, чтобы изменить ширину меню"
        />
        <NavLink
          to="/dashboard"
          className="ds-sidebar__logo"
          aria-label="SENSOR"
          style={{ justifyContent: 'flex-start' }}
        >
          {/* Same wordmark logo as the landing site — larger, left-aligned, and
              shifted right ~10px so its left edge lines up with the org avatar below. */}
          <img
            src="/sensor-logo.png"
            alt="SENSOR"
            style={{ height: 38, width: 'auto', display: 'block', marginLeft: 10 }}
          />
        </NavLink>

        {/* Org switcher — readable name + tier sub-line, with a popover listing
            the user's organizations (switching/management happens in the bot). */}
        <div className="ds-org-wrap" ref={orgMenuRef}>
          <button
            type="button"
            className={orgMenuOpen ? 'ds-org is-open' : 'ds-org'}
            aria-label="Организация"
            aria-haspopup="menu"
            aria-expanded={orgMenuOpen}
            onClick={() => setOrgMenuOpen((v) => !v)}
          >
            <span className="ds-org__avatar">{orgBadge}</span>
            <span className="ds-org__name">{orgLabel}</span>
            {user?.tier && <span className="ds-org__sub">{user.tier}</span>}
            <span className="ds-org__caret">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M7 9l5 5 5-5" />
              </svg>
            </span>
          </button>
          <div className={orgMenuOpen ? 'org-pop is-open' : 'org-pop'} role="menu">
            <div className="org-pop__label">Организации</div>
            {orgs.length > 0 ? (
              orgs.map((o) => (
                <div
                  key={o.id}
                  className={
                    o.id === user?.active_organization_id
                      ? 'org-pop__item is-current'
                      : 'org-pop__item'
                  }
                >
                  <span className="avatar">
                    {(o.name || 'O').trim().charAt(0).toUpperCase()}
                  </span>
                  <span className="org-pop__meta">
                    <b>{o.name || 'Организация'}</b>
                    <small>{o.role}</small>
                  </span>
                </div>
              ))
            ) : (
              <div className="org-pop__item is-current">
                <span className="avatar">{orgBadge}</span>
                <span className="org-pop__meta">
                  <b>{orgLabel}</b>
                  <small>{user?.tier ?? ''}</small>
                </span>
              </div>
            )}
            <div className="org-pop__sep" />
            <a
              className="org-pop__action"
              href="https://t.me/sensor_tg_bot"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M16 12h6m-3-3 3 3-3 3M11 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" />
              </svg>
              Управление в боте
            </a>
          </div>
        </div>

        <div className="ds-nav-label">Рабочее место</div>
        <nav className="ds-nav">
          {NAV_WORKPLACE.map((item) => {
            const locked = item.to === '/audience' && !!user && !user.has_audience
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => (isActive ? 'is-active' : undefined)}
              >
                <span className="icon">{item.icon}</span>
                {item.label}
                {locked && (
                  <span
                    title="Доступно на Pro"
                    style={{
                      marginLeft: 'auto',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      letterSpacing: '0.04em',
                      color: 'var(--muted)',
                      background: 'var(--bg-warm)',
                      borderRadius: 999,
                      padding: '2px 7px',
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                      <rect x="5" y="11" width="14" height="9" rx="2" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    </svg>
                    PRO
                  </span>
                )}
                {item.count !== undefined && <span className="count">{item.count}</span>}
              </NavLink>
            )
          })}
          {!!user?.is_sensor_admin && (
            <NavLink
              to="/admin/senders"
              className={({ isActive }) => (isActive ? 'is-active' : undefined)}
            >
              <span className="icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </span>
              Senders (админ)
            </NavLink>
          )}
        </nav>

        <div className="ds-sidebar__bottom">
          <NavLink
            to="/settings"
            className={({ isActive }) => (isActive ? 'is-active' : undefined)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 8,
              textDecoration: 'none',
              color: 'var(--ink-2)',
              marginBottom: 8,
              fontSize: 13,
            }}
          >
            <span className="icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
              </svg>
            </span>
            Настройки
          </NavLink>
          {user?.tier && user.tier !== 'free' ? (
            <div className={`ds-tier ${user.tier === 'trial' ? 'is-trial' : ''}`}>
              <span className="ds-tier__label">Тариф</span>
              <span className="ds-tier__value">{user.tier.charAt(0).toUpperCase() + user.tier.slice(1)}</span>
            </div>
          ) : (
            <div className="ds-trial--mini">
              <div className="top">
                <b>Триал</b>
                <small>демо</small>
              </div>
              <div className="ds-trial__bar">
                <i style={{ width: '50%' }} />
              </div>
              <div className="ds-trial__txt">
                Подключите тариф, чтобы не потерять источники.
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => void logout()}
            className="btn btn--ghost btn--sm"
            style={{ justifyContent: 'center', marginTop: 8, width: '100%' }}
          >
            Выйти
          </button>
        </div>
      </aside>

      {/* ============================================================
          Main column (topbar + content)
          ============================================================ */}
      <div>
        <header className="ds-topbar">
          <div className="ds-topbar__crumbs">
            <span>SENSOR</span>
            <span className="sep">/</span>
            <b id="crumb-current">{crumb}</b>
          </div>

          {/* Global search is a future feature (needs a backend search endpoint);
              the working per-page search lives on the Leads screen. */}
          <div style={{ flex: 1 }} />

          <div className="ds-topbar__right">
            <div className="lang-switch" role="group" aria-label="Language">
              <button
                type="button"
                className={lang === 'ru' ? 'lang-btn is-active' : 'lang-btn'}
                aria-pressed={lang === 'ru'}
                data-lang="ru"
                onClick={() => setLang('ru')}
              >
                RU
              </button>
              <button
                type="button"
                className={lang === 'en' ? 'lang-btn is-active' : 'lang-btn'}
                aria-pressed={lang === 'en'}
                data-lang="en"
                onClick={() => setLang('en')}
              >
                EN
              </button>
            </div>

            <a
              href="https://t.me/sensor_tg_bot"
              className="btn btn--ghost btn--sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                style={{ width: 14, height: 14 }}
              >
                <path d="M9.78 18.65 9.99 15.42l5.83-5.26c.26-.23-.06-.34-.4-.14L8.21 14.5l-3.11-.97c-.67-.21-.68-.67.15-1L19.66 7c.56-.21 1.09.13.88.96l-2.7 12.74c-.18.85-.69 1.06-1.4.66l-3.81-2.82-1.84 1.78c-.21.21-.39.39-.81.39z" />
              </svg>
              Открыть бота
            </a>

            {/* Notifications removed until there is a real notification feed. */}

            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="ds-avatar"
                title={userIdLabel}
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                style={{ border: 'none', padding: 0, cursor: 'pointer' }}
              >
                {/* No display name from Telegram auth — use a neutral person glyph
                    instead of raw id digits. */}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  style={{ width: 18, height: 18 }}
                  aria-hidden="true"
                >
                  <circle cx="12" cy="8" r="3.5" />
                  <path d="M5 20a7 7 0 0 1 14 0" />
                </svg>
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    minWidth: 210,
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-md)',
                    padding: 6,
                    zIndex: 50,
                  }}
                >
                  <div
                    style={{
                      padding: '8px 10px',
                      borderBottom: '1px solid var(--border)',
                      marginBottom: 4,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                      {orgLabel}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                      {userIdLabel || '—'}
                    </div>
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false)
                      void logout()
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      fontSize: 13,
                      color: 'var(--ink-2)',
                      background: 'none',
                      border: 'none',
                      borderRadius: 'var(--radius)',
                      cursor: 'pointer',
                    }}
                  >
                    Выйти
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="ds-content">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
